import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';

// B2C 쿠폰/포인트 사용관리 메뉴 추가됨
const MENU_ITEMS = [
    {
        key: 'dashboard',
        label: '통계현황',
        path: '/dashboard',
    },
    {
        key: 'notice_event',
        label: '공지/이벤트',
        sub: [
            { label: '유저공지', path: '/notice', permission: 'user_notice_manage' },
            { label: '유저이벤트', path: '/event', permission: 'user_event_manage' },
            { label: '매니저공지', path: '/manager-notices', permission: 'manager_notice_manage' },
            { label: '매니저이벤트', path: '/manager-events', permission: 'manager_event_manage' },
        ]
    },
    {
        key: 'b2c_orders',
        label: 'B2C주문',
        sub: [
            { label: '주문관리', path: '/totalreserve', permission: 'total_reservation_manage' },
            { label: 'AS관리', path: '/reserve_as', permission: 'as_manage' },
            { label: '쿠폰사용관리', path: '/coupon_usage', permission: 'coupon_manage' },
            { label: '포인트사용관리', path: '/point_usage', permission: 'coupon_manage' },
        ]
    },
    {
        key: 'b2b_orders',
        label: 'B2B주문',
        sub: [
            { label: '주문관리', path: '/public_reservation', permission: 'bulk_order_manage' },
            { label: '견적서', path: '/quotation', permission: 'quotation_manage' },
            { label: '견적서관리', path: '/quotation_list', permission: 'quotation_manage' },
            { label: '공고관리', path: '/notice_list', permission: 'bulk_order_manage' },
        ]
    },
    {
        key: 'settlement',
        label: '정산관리',
        sub: [
            { label: '일별관리', path: '/biling', permission: 'bilings' },
            { label: '리스트관리', path: '/total_biling', permission: 'bilings' },
        ]
    },
    {
        key: 'account',
        label: '계정관리',
        sub: [
            { label: '고객관리', path: '/user', permission: 'user_manage' },
            { label: '고객탈퇴', path: '/account_deletion', permission: 'user_leave_manage' },
            { label: '매니저관리', path: '/manager', permission: 'manager_manage' },
            { label: '매니저가입요청', path: '/request_manager', permission: 'manager_request_manage' },
            { label: '직원관리', path: '/staff', adminOnly: true },
        ]
    },
    {
        key: 'operation',
        label: '운영관리',
        sub: [
            { label: '요금관리', path: '/cost', permission: 'cost_manage' },
            { label: '교육관리', path: '/training', permission: 'training_manage' },
            { label: '팝업관리', path: '/popup_settings', permissionAny: ['popup_manage', 'benefit_event_manage'] },
            { label: '앱 버전 관리', path: '/app_version_settings', permission: 'app_version_manage' },
            { label: '쿠폰관리', path: '/coupon', permission: 'coupon_manage', unsupported: true },
            { label: '액션로그', path: '/action_log', permission: 'action_log_view' },
        ]
    },
];

// 서브 항목 접근 가능 여부. permission(단일 키)과 permissionAny(키 중 하나) 모두 지원한다.
const hasSubAccess = (subItem, role, permissions) => {
    if (role === 'admin') return true;
    if (subItem.adminOnly) return false;
    const keys = subItem.permissionAny || (subItem.permission ? [subItem.permission] : []);
    if (keys.length === 0) return true;
    // Whitelist 방식: permissions가 로드되지 않았거나(null) 해당 키가 없으면(undefined) 숨김
    return Boolean(permissions) && keys.some((key) => permissions[key]);
};

const Menu = ({
    loginId,
    role,
    loginName,
    loginAffiliation,
    loginPosition,
    permissions,
}) => { // permissions prop 추가
    const [selectedMenu, setSelectedMenu] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    // 현재 경로에 맞는 상위 메뉴 자동 펼침 (URL 직접 접속/새로고침 시에도 동작)
    useEffect(() => {
        const parentMenu = MENU_ITEMS.find(menu =>
            menu.sub?.some(sub => sub.path === location.pathname)
        );
        if (parentMenu) {
            setSelectedMenu(parentMenu.key);
        } else {
            const topMenu = MENU_ITEMS.find(menu => menu.path === location.pathname);
            if (topMenu) {
                setSelectedMenu(topMenu.key);
            }
        }
    }, [location.pathname]);

    const handleMenuClick = (menu) => {
        if (menu.sub) {
            setSelectedMenu(menu.key === selectedMenu ? null : menu.key);
        } else {
            setSelectedMenu(menu.key);
            navigate(menu.path, { state: { refreshKey: Date.now() } });
        }
    };

    const handleLogout = () => {
        // 로그아웃 처리
        localStorage.clear();
        sessionStorage.clear();
        navigate('/');
    };

    return (
        <MenuContainer>
            <LogoSection onClick={() => navigate('/dashboard', { state: { refreshKey: Date.now() } })}>
                <Logo
                    src="/title_icon.png"
                    alt="Title"
                    id="title-image"
                />
            </LogoSection>
            <MenuWrapper>
                <MainMenuBar>
                    {MENU_ITEMS.filter((menu) => {
                        // sub가 없는 메뉴(dashboard 등)는 항상 표시
                        if (!menu.sub) return true;
                        // admin은 모든 메뉴 표시
                        if (role === 'admin') return true;
                        // sub 중 하나라도 접근 가능한 항목이 있으면 표시
                        return menu.sub.some((subItem) => hasSubAccess(subItem, role, permissions));
                    }).map((menu) => (
                        <MainMenuItem
                            key={menu.key}
                            onClick={() => handleMenuClick(menu)}
                            $isActive={selectedMenu === menu.key}
                        >
                            {menu.label}
                        </MainMenuItem>
                    ))}
                </MainMenuBar>
                <SubMenuBar $isVisible={selectedMenu !== null}>
                    {MENU_ITEMS.filter((m) => m.key === selectedMenu && m.sub).map((menu) =>
                        menu.sub.map((subItem) => {
                            // 권한 체크
                            if (!hasSubAccess(subItem, role, permissions)) return null;

                            // 지원하지 않는 기능 체크
                            if (subItem.unsupported) {
                                return (
                                    <SubMenuItem
                                        key={subItem.path}
                                        onClick={() => alert('현재 지원하지 않는 기능입니다.')}
                                        style={{ cursor: 'pointer', opacity: 0.6 }}
                                    >
                                        {subItem.label}
                                    </SubMenuItem>
                                );
                            }

                            return (
                                <SubMenuItem
                                    key={subItem.path}
                                    $isActive={location.pathname === subItem.path}
                                    onClick={() => navigate(subItem.path, { state: { refreshKey: Date.now() } })}
                                >
                                    {subItem.label}
                                </SubMenuItem>
                            );
                        })
                    )}
                </SubMenuBar>
            </MenuWrapper>
            <UserInfo>
                <UserDetails role="group" aria-label="로그인 사용자 정보">
                    {loginAffiliation && (
                        <UserAffiliation title={loginAffiliation}>
                            {loginAffiliation}
                        </UserAffiliation>
                    )}
                    <UserIdentityLine>
                        <UserName>{loginName || loginId}</UserName>
                        {loginPosition && <UserPosition>{loginPosition}</UserPosition>}
                    </UserIdentityLine>
                </UserDetails>
                <LogoutButton onClick={handleLogout}>로그아웃</LogoutButton>
            </UserInfo>
        </MenuContainer>
    );
};

// Styled Components
const MenuContainer = styled.div`
    background: #ffffff;
    display: flex;
    align-items: center;
    padding: 0 30px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    min-height: 90px;
    border-bottom: 1px solid #e8e8e8;
`;

const LogoSection = styled.div`
    display: flex;
    align-items: center;
    margin-right: 50px;
    cursor: pointer;
`;

const Logo = styled.img`
    width: 200px;
    transition: transform 0.3s ease;
    
    &:hover {
        transform: scale(1.05);
    }
`;

const MenuWrapper = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0px;
`;

const MainMenuBar = styled.div`
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
`;

const MainMenuItem = styled.button`
    background: transparent;
    border: none;
    color: ${props => props.$isActive ? '#007bff' : '#555'};
    padding: 12px 8px;
    font-size: 16px;
    font-weight: ${props => props.$isActive ? '700' : '600'};
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    position: relative;
    
    &:after {
        content: '';
        position: absolute;
        bottom: 8px;
        left: 50%;
        transform: translateX(-50%);
        width: ${props => props.$isActive ? '100%' : '0'};
        height: 2px;
        background: #007bff;
        transition: width 0.2s ease;
    }
    
    &:hover {
        color: #007bff;
        
        &:after {
            width: 100%;
        }
    }
`;

const SubMenuBar = styled.div`
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    max-height: ${props => props.$isVisible ? '200px' : '0'};
    opacity: ${props => props.$isVisible ? '1' : '0'};
    overflow: hidden;
    transition: all 0.3s ease;
    padding-left: 12px;
    margin-top: ${props => props.$isVisible ? '4px' : '0'};
`;


const SubMenuItem = styled.button`
    background: transparent;
    border: none;
    color: ${props => props.$isActive ? '#007bff' : '#666'};
    padding: 6px 8px;
    font-size: 14px;
    font-weight: ${props => props.$isActive ? '700' : '500'};
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        color: #007bff;
    }
`;

const UserInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 20px;
    background: #f8f9fa;
    border-radius: 10px;
    border: 1px solid #e0e0e0;
`;

const UserDetails = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    min-width: 140px;
    max-width: 220px;
`;

const UserAffiliation = styled.div`
    width: 100%;
    overflow: hidden;
    color: #333;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.25;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const UserIdentityLine = styled.div`
    display: flex;
    align-items: baseline;
    gap: 14px;
`;

const UserName = styled.div`
    font-size: 16px;
    font-weight: 500;
    color: #333;
    white-space: nowrap;
`;

const UserPosition = styled.div`
    font-size: 16px;
    font-weight: 500;
    color: #333;
    white-space: nowrap;
`;

const LogoutButton = styled.button`
    background: #007bff;
    color: white;
    border: none;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 600;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    
    &:hover {
        background: #0056b3;
    }
    
    &:active {
        transform: scale(0.98);
    }
`;

export default Menu;
